import { Quote, Shield } from "lucide-react";

// De-identified placeholder reflections. Replace with actual de-identified
// Google review / SimplePractice testimonials when available.
const reflections = [
  {
    body:
      "I came in feeling stuck and not sure how to even describe what I needed. The space I found here helped me slow down, name what was happening, and start moving forward with more clarity. I leave each session feeling steadier.",
    label: "Verified Client",
  },
  {
    body:
      "What stood out most was how structured and supportive the work felt. I wasn't just venting — I was learning to understand myself in a way that's actually changing how I show up day to day.",
    label: "Therapy Client",
  },
  {
    body:
      "Supervision here has been one of the most growth-filled experiences of my career. I feel more grounded, more ethical, and more confident in my clinical decisions than I did six months ago.",
    label: "Supervision Participant",
  },
  {
    body:
      "I appreciated how culturally aware and human the work felt. There was room for all of me — my faith, my family, my career — without judgment. That made it easier to keep coming back.",
    label: "Former Client",
  },
  {
    body:
      "Coaching here helped me move from spinning ideas in my head to actually executing on them. Clear plan, gentle accountability, and someone who could see the bigger picture with me.",
    label: "Coaching Client",
  },
  {
    body:
      "I was nervous about starting. I left my first session feeling heard, not analyzed. That difference matters more than I knew how to articulate before.",
    label: "Verified Client",
  },
];

export default function Testimonials() {
  return (
    <section
      data-testid="testimonials-section"
      className="py-24 md:py-32 bg-surface relative overflow-hidden"
    >
      <div className="absolute inset-0 opacity-[0.05] pointer-events-none" style={{
        backgroundImage: "radial-gradient(#3B2E5A 1px, transparent 1px)",
        backgroundSize: "32px 32px",
      }} />
      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-3xl mb-14">
          <p className="overline mb-5">Client Reflections</p>
          <h2 className="font-display text-4xl md:text-6xl text-forest font-medium leading-[1.05] tracking-tight">
            Words from those <em className="italic text-terracotta">we've walked with.</em>
          </h2>
          <p className="mt-6 text-ink/75 leading-relaxed text-lg">
            At Seven Pillars of Life, client privacy matters. Reflections shared here are de-identified and presented without personal health information.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {reflections.map((r, i) => (
            <figure
              key={i}
              data-testid={`reflection-card-${i}`}
              className={`p-8 md:p-9 rounded-2xl flex flex-col justify-between min-h-[260px] transition-all duration-300 ${
                i === 1 || i === 4
                  ? "bg-forest text-bone hover:bg-[#5D4B85]"
                  : "bg-bone border border-sage/25 hover:border-terracotta/40 hover:shadow-xl"
              }`}
            >
              <Quote
                size={28}
                strokeWidth={1.4}
                className={`mb-5 ${i === 1 || i === 4 ? "text-terracotta" : "text-terracotta/80"}`}
              />
              <blockquote className={`font-display text-xl md:text-[22px] leading-snug ${i === 1 || i === 4 ? "text-bone" : "text-forest"}`}>
                "{r.body}"
              </blockquote>
              <figcaption
                className={`mt-7 text-xs uppercase tracking-[0.22em] font-semibold ${i === 1 || i === 4 ? "text-terracotta" : "text-terracotta"}`}
              >
                — {r.label}
              </figcaption>
            </figure>
          ))}
        </div>

        <div
          data-testid="testimonials-privacy-note"
          className="mt-14 flex items-start gap-4 max-w-3xl mx-auto bg-bone/60 border border-sage/30 rounded-2xl p-6"
        >
          <Shield size={20} strokeWidth={1.5} className="text-terracotta shrink-0 mt-1" />
          <p className="text-sm text-ink/70 leading-relaxed italic">
            Client reflections are shared in a de-identified format to protect privacy and avoid disclosure of personal health information.
          </p>
        </div>
      </div>
    </section>
  );
}
